import React, { useState, KeyboardEvent, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@radix-ui/themes";
import { Send } from "lucide-react";

interface WalkthroughProgressBarProps {
  currentStep: number;
  totalSteps?: number;
  isPaused: boolean;
  onClickStop: () => void;
  onClickContinue: () => void;
  isLoading?: boolean;
  followupQuestion?: string;
  onSendFollowupResponse: (response: string) => void;
}

interface Position {
  x: number;
  y: number;
}

export function WalkthroughProgressBar({
  currentStep,
  totalSteps = 5,
  isPaused,
  onClickStop,
  onClickContinue,
  isLoading = false,
  followupQuestion,
  onSendFollowupResponse,
}: WalkthroughProgressBarProps) {
  const [inputValue, setInputValue] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const [position, setPosition] = useState<Position>(() => {
    // Load saved position from localStorage, or default to top center
    const saved = localStorage.getItem("quack-topbar-position");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { x: window.innerWidth / 2, y: 0 };
      }
    }
    return { x: window.innerWidth / 2, y: 0 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("quack-topbar-position", JSON.stringify(position));
  }, [position]);

  // Handle mouse events for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        // Keep within viewport bounds
        const maxX = window.innerWidth - (containerRef.current?.offsetWidth || 0);
        const maxY = window.innerHeight - (containerRef.current?.offsetHeight || 0);

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleClose = () => {
    onClickStop(); // Stop the walkthrough
    setIsVisible(false); // Hide the topbar
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      onSendFollowupResponse(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  const isFollowupQuestion = followupQuestion && followupQuestion.trim() !== "";

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  const content = (
    <>
      <style>{`
        @keyframes text-shimmer {
          0% { background-position: 100% 0; }
          100% { background-position: 0 0; }
        }
        @keyframes agent-glow {
          0% { opacity: 0.1; }
          50% { opacity: 0.25; }
          100% { opacity: 0.1; }
        }
        .agent-overlay {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 999998;
          background: linear-gradient(
            45deg, 
            #4F7FFF 0%,
            #9D4FFF 50%,
            #FF4FB8 100%
          );
          -webkit-mask-image: radial-gradient(circle at center, transparent 40%, black 100%);
          mask-image: radial-gradient(circle at center, transparent 40%, black 100%);
          animation: agent-glow 6s infinite ease-in-out;
          transition: opacity 0.5s ease;
        }
        .text-shimmer {
          color: transparent !important;
          background: linear-gradient(
            90deg, 
            rgba(255, 255, 255, 0.4) 33%, 
            rgba(255, 255, 255, 1) 50%, 
            rgba(255, 255, 255, 0.4) 66%
          );
          background-size: 300% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          animation: text-shimmer 3s infinite linear;
        }
      `}</style>

      {/* Full-page Agent Glow Overlay */}
      {!isPaused && <div className="agent-overlay" />}

      <div
        ref={containerRef}
        data-walkthrough-ui="true"
        style={{
          position: "fixed",
          top: `${position.y}px`,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 999999,
          backgroundColor: "rgba(0, 0, 0, 0.95)",
          padding: "8px 12px",
          paddingBottom: "12px",
          borderTopLeftRadius: "0px",
          borderTopRightRadius: "0px",
          borderBottomLeftRadius: "8px",
          borderBottomRightRadius: "8px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
          display: "flex",
          flexDirection: "column",
          minWidth: "min(280px, 90vw)",
          maxWidth: "45vw",
          backdropFilter: "blur(12px)",
          cursor: isDragging ? "grabbing" : "default",
          userSelect: "none",
          marginLeft: "auto",
          marginRight: "auto",
          alignItems: "stretch",
        }}
      >
        {/* Drag Handle
      <div
        onMouseDown={handleMouseDown}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isDragging ? "grabbing" : "grab",
          padding: "2px 0",
          marginBottom: "2px",
          borderRadius: "4px",
          transition: "background-color 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <GripVertical size={20} color="rgba(255, 255, 255, 0.4)" />
      </div> */}

        {/* Progress indicators row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            width: "100%",
            padding: "0 2px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {Array.from({
              length: currentStep === 0 ? 2 : currentStep + 2,
            }).map((_, index) => {
              const isActive = index === currentStep;
              const isFuture = index > currentStep;

              return (
                <div
                  key={index}
                  style={{
                    width: isActive ? "24px" : "6px",
                    height: "6px",
                    borderRadius: "1px",
                    backgroundColor: isFuture ? "rgba(255, 255, 255, 0.2)" : "#4F7FFF",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: isActive ? "0 0 8px rgba(79, 127, 255, 0.6)" : "none",
                    flexShrink: 0,
                  }}
                />
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isPaused && (
              <Button
                size="1"
                variant="solid"
                onClick={onClickContinue}
                disabled={isLoading}
                style={{
                  fontSize: "12px",
                  padding: "4px 12px",
                  height: "28px",
                  cursor: "pointer",
                  borderRadius: "6px",
                  fontWeight: 500,
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  border: "none",
                  transition: "transform 0.1s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                {isLoading ? "Loading..." : "Resume"}
              </Button>
            )}

            <Button
              size="1"
              variant="soft"
              onClick={handleClose}
              disabled={isLoading}
              style={{
                fontSize: "12px",
                padding: "4px 12px",
                height: "28px",
                cursor: "pointer",
                fontWeight: 500,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "rgba(255, 255, 255, 0.9)",
                border: "none",
                borderRadius: "6px",
                transition: "transform 0.1s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              Stop
            </Button>
          </div>
        </div>

        {/* Main controls row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            width: "100%",
          }}
        >
          <div>
            {!isFollowupQuestion && (
              <p
                className={!isPaused ? "text-shimmer" : ""}
                style={{
                  fontSize: "13px",
                  color: isPaused ? "rgb(125 125 125)" : undefined,
                  fontFamily: "Inter, system-ui, sans-serif",
                  margin: 0,
                  fontWeight: 500,
                }}
              >
                {`Walkthrough ${isPaused ? "paused" : "in progress..."}`}
              </p>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Close button */}
            {/* <button
              onClick={handleClose}
              disabled={isLoading}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                opacity: isLoading ? 0.5 : 0.8,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.opacity = "1";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.opacity = "0.8";
              }}
            >
              <X size={16} color="white" />
            </button> */}
          </div>
        </div>

        {isFollowupQuestion && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div
              style={{
                fontSize: "14px",
                outline: "none",
                fontFamily: "Inter, system-ui, sans-serif",
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                lineHeight: "1.5",
                color: "rgba(255, 255, 255, 0.9)",
                maxWidth: "75%",
              }}
            >
              {followupQuestion}
            </div>

            {/* Follow-up question input */}
            <div
              style={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                border: isInputFocused
                  ? "1px solid rgb(92 127 255 / 50%)"
                  : "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "10px",
                transition: "border-color 0.2s ease",
              }}
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                placeholder={"What are your thoughts?"}
                disabled={isLoading}
                style={{
                  flex: 1,
                  fontSize: "13px",
                  backgroundColor: "transparent",
                  color: "rgba(255, 255, 255, 0.95)",
                  padding: "4px 12px",
                  border: "none",
                  outline: "none",
                  fontFamily: "Inter, system-ui, sans-serif",
                }}
              />
              <Button
                size="2"
                variant="solid"
                onClick={handleSend}
                disabled={isLoading || !inputValue.trim()}
                style={{
                  cursor: "pointer",
                  backgroundColor: "#212225",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "10px",
                  borderTopLeftRadius: "0px",
                  borderBottomLeftRadius: "0px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && inputValue.trim()) {
                    e.currentTarget.style.backgroundColor = "#6B8FFF";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#5B7FFF";
                }}
              >
                <Send size={14} color="white" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Render using portal to attach to document.body
  return createPortal(content, document.body);
}
